import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import MenuTop from '../../components/menu/top/menu-top';
import './style.css';
import { fetchProjectsGroupedService, fetchProjectsService } from '../../features/project/service/project-service';
import Loader from '../../components/loader/loader';
import AccordionComponent from '../../components/accordion/accordion';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/inputs/common/input';
import storage from '../../shared/services/storage';
import { fetchUserService } from '../../features/users/service/users-service';

type StatusFilter = 'all' | 'in-progress' | 'finished';

interface DashboardProject {
    id: string;
    name: string;
    isFinished?: boolean;
    principalInvestigatorName?: string;
}

interface DashboardGroup {
    principalInvestigator?: {
        name?: string;
    };
    company?: {
        name?: string;
    };
    projects: DashboardProject[];
}

const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
    { value: 'all', label: 'Todos' },
    { value: 'in-progress', label: 'Em andamento' },
    { value: 'finished', label: 'Concluídos' },
];

const enrichProjectsWithStatus = (groupedProjects: DashboardGroup[], projectsWithStatus: DashboardProject[]): DashboardGroup[] => {
    const statusByProjectId = new Map(
        (projectsWithStatus || []).map((project) => [project.id, Boolean(project.isFinished)])
    );

    return (groupedProjects || []).map((group) => ({
        ...group,
        projects: (group.projects || []).map((project) => ({
            ...project,
            isFinished: statusByProjectId.get(project.id) ?? Boolean(project.isFinished),
        })),
    }));
};

function Home() {
    const [ projects, setProjects ] = useState<DashboardGroup[]>([]);
    const [ isLoading, setIsLoading ] = useState(true);
    const [ filterValue, setFilterValue ] = useState('');
    const [ assignedProjectIds, setAssignedProjectIds ] = useState<string[]>([]);
    const [ isSecretariaUser, setIsSecretariaUser ] = useState(false);
    const [ statusFilter, setStatusFilter ] = useState<StatusFilter>('all');
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const navigate = useNavigate();

    // Função para garantir tempo mínimo de loading
    const setExtendedLoading = (duration: number = 800) => {
        // Limpa qualquer timeout existente
        if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
        }
        
        // Ativa o loading
        setIsLoading(true);
        
        // Define um novo timeout que vai desativar o loading após a duração especificada
        loadingTimeoutRef.current = setTimeout(() => {
            setIsLoading(false);
            loadingTimeoutRef.current = null;
        }, duration);
    };

    // Limpa o timeout quando o componente é desmontado
    useEffect(() => {
        return () => {
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }
        };
    }, []);

    const loadProjects = useCallback(async (search: string = '') => {
        setExtendedLoading();
        
        try {
            const [groupedProjects, projectsWithStatus] = await Promise.all([
                fetchProjectsGroupedService(search),
                fetchProjectsService(search),
            ]);

            setProjects(enrichProjectsWithStatus(groupedProjects, projectsWithStatus));
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
    }, []);

    // Efeito para carregar projetos iniciais
    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const storedUser = JSON.parse(storage.getStorage('user') || '{}');

                if (!storedUser?.id) {
                    return;
                }

                const currentUser = await fetchUserService(storedUser.id);
                const roleName = currentUser?.role?.name?.toLowerCase() || '';
                const nextAssignedProjectIds = currentUser?.projectIds || currentUser?.assignedProjects?.map((project) => project.id) || [];

                setIsSecretariaUser(roleName === 'secretaria');
                setAssignedProjectIds(nextAssignedProjectIds);
            } catch (error) {
                console.log(error);
            }
        };

        fetchCurrentUser();
    }, []);

    // Utilizar debounce para evitar múltiplas requisições rápidas enquanto o usuário digita
    const debounce = (fn: Function, delay: number) => {
        let timer: NodeJS.Timeout;
        return (...args: any[]) => {
            clearTimeout(timer);
            timer = setTimeout(() => fn(...args), delay);
        };
    };

    // Função para buscar projetos com debounce
    const debouncedFetchProjects = useCallback(
        debounce(async (value: string) => {
            await loadProjects(value);
        }, 500), // 500ms de delay para permitir digitação contínua
        [loadProjects]
    );

    const handleRedirect = (id: string) => {
        navigate(`/projeto-list/${id}`);
    }

    const handleChange = ({ target: { value }}: any) => {
        setFilterValue(value);
        debouncedFetchProjects(value);
    }

    const filteredProjects = useMemo(() => {
        if (statusFilter === 'all') {
            return projects;
        }

        const shouldBeFinished = statusFilter === 'finished';

        return projects
            .map((group) => ({
                ...group,
                projects: (group.projects || []).filter((project) => Boolean(project.isFinished) === shouldBeFinished),
            }))
            .filter((group) => group.projects.length > 0);
    }, [projects, statusFilter]);

    // Determina se há um filtro ativo
    const isFiltered = filterValue.trim() !== '' || statusFilter !== 'all';

    const isFullWidth = window.innerWidth > 768 ? 900 : window.innerWidth - 40;

    const quickAccessProjects = useMemo(() => {
        if (!isSecretariaUser || !assignedProjectIds.length) {
            return [];
        }

        return filteredProjects
            .flatMap((group) =>
                (group.projects || []).map((project) => ({
                    id: project.id,
                    name: project.name,
                    principalInvestigatorName: group.principalInvestigator?.name || '',
                }))
            )
            .filter((project) => assignedProjectIds.includes(project.id));
    }, [assignedProjectIds, filteredProjects, isSecretariaUser]);

    return (
        <div className="container-home">
            <MenuTop />
            {isLoading && <Loader />}
            <div className='container-home__content'>
                <div className="container-home__content__filter">
                    <Input 
                        placeholder="Filtrar projetos" 
                        onChange={handleChange} 
                        icon={true} 
                        value={filterValue}
                        width={isFullWidth}
                    />
                    <div className="home-status-chips" role="group" aria-label="Filtrar por status">
                        {STATUS_FILTER_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                className={`home-status-chip ${statusFilter === option.value ? 'home-status-chip--active' : ''}`}
                                onClick={() => setStatusFilter(option.value)}
                                aria-pressed={statusFilter === option.value}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                {isSecretariaUser && quickAccessProjects.length > 0 && (
                    <div className="home-quick-access">
                        <div className="home-quick-access__header">
                            <h3>Projetos com acesso</h3>
                        </div>
                        <div className="home-quick-access__grid">
                            {quickAccessProjects.map((project) => (
                                <button
                                    key={project.id}
                                    type="button"
                                    className="home-quick-access__card"
                                    onClick={() => handleRedirect(project.id)}
                                >
                                    <span className="home-quick-access__title">{project.name}</span>
                                    <span className="home-quick-access__pi">
                                        {project.principalInvestigatorName}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className={`accordion-wrapper ${isLoading ? 'hidden' : 'visible'}`}>
                    <AccordionComponent 
                        data={filteredProjects} 
                        handleRedirect={handleRedirect} 
                        isFiltered={isFiltered} 
                    />
                </div>
            </div>
        </div>
    );
}

export default Home;
